import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Truck, ShieldCheck, Flame } from "lucide-react";
import { api, CATEGORIES } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const HERO = "https://images.unsplash.com/photo-1765045768265-e3eb8471fce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SIDE = "https://images.unsplash.com/photo-1544003484-3cd181d17917?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
const SIDE2 = "https://images.unsplash.com/photo-1599075727766-fec27a2ae57a?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const Marquee = () => (
  <div className="border-y border-white/10 bg-[#FF3B30] overflow-hidden py-3">
    <div className="marquee flex whitespace-nowrap w-max">
      {Array.from({ length: 2 }).map((_, k) => (
        <div key={k} className="flex">
          {["Sculpt harder", "Rep the logo", "Free shipping $75+", "Built for the grind", "Awesome Body Sculpting"].map((t) => (
            <span key={t} className="font-display uppercase font-black text-2xl text-black px-8">{t} ✕</span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => { api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)); }, []);

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-8 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 relative min-h-[520px] overflow-hidden border border-white/10 rise">
          <img src={HERO} alt="ABS athlete" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="relative h-full flex flex-col justify-end p-8 sm:p-12 min-h-[520px]">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-4">Official ABS Gym Merch · Drop 01</p>
            <h1 className="font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl text-white max-w-2xl">
              Wear the work.<br />Rep the <span className="text-[#FF3B30]">ABS</span>.
            </h1>
            <p className="text-neutral-300 mt-5 max-w-md text-base">Shirts, bands, bottles and gear stamped with the Awesome Body Sculpting logo — plus training programs, supplements and meal plans to match. Train in it. Live in it.</p>
            <div className="flex gap-3 mt-8">
              <Link to="/shop" data-testid="hero-shop-button" className="h-12 px-7 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-display uppercase font-black tracking-wider text-lg flex items-center gap-2 transition-colors">
                Shop now <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/shop?category=apparel" data-testid="hero-apparel-button" className="h-12 px-7 border border-white/30 hover:bg-white hover:text-black text-white font-display uppercase font-bold tracking-wider text-lg grid place-items-center transition-colors">
                Apparel
              </Link>
            </div>
          </div>
        </div>
        <div className="lg:col-span-4 grid grid-rows-2 gap-4">
          {[{ img: SIDE, label: "Hydration", cat: "bottles" }, { img: SIDE2, label: "Wrist & Headbands", cat: "bands" }].map((b, i) => (
            <Link key={b.cat} to={`/shop?category=${b.cat}`} data-testid={`hero-tile-${b.cat}`} className="group relative overflow-hidden border border-white/10 min-h-[250px] rise" style={{ animationDelay: `${150 + i * 120}ms` }}>
              <img src={b.img} alt={b.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-transparent" />
              <div className="absolute bottom-0 p-6 flex items-end justify-between w-full">
                <h3 className="font-display uppercase font-black text-3xl text-white">{b.label}</h3>
                <ArrowRight className="w-6 h-6 text-[#FF3B30] transition-transform group-hover:translate-x-1" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#FF3B30] font-semibold mb-2">Best sellers</p>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl">Featured drop</h2>
          </div>
          <Link to="/shop" data-testid="view-all-link" className="font-display uppercase font-bold tracking-wider text-neutral-300 hover:text-white flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featured.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap gap-3 mb-16">
          {CATEGORIES.filter((c) => c.id !== "all").map((c) => (
            <Link key={c.id} to={`/shop?category=${c.id}`} data-testid={`home-category-${c.id}`}
              className="px-6 py-4 border border-white/10 hover:border-[#FF3B30] hover:text-white font-display uppercase font-bold text-xl tracking-wide text-neutral-300 transition-colors">
              {c.label}
            </Link>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[[Truck, "Free shipping $75+", "Tracked delivery on physical orders over $75. Digital programs and meal plans arrive instantly."], [ShieldCheck, "Secure checkout", "Payments processed safely by Stripe."], [Flame, "Gym-tested", "Every piece survives ABS training sessions."]].map(([Icon, t, d]) => (
            <div key={t} className="bg-[#09090B] p-8">
              <Icon className="w-6 h-6 text-[#FF3B30] mb-4" />
              <h3 className="font-display uppercase font-bold text-xl">{t}</h3>
              <p className="text-sm text-neutral-400 mt-1">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
